import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdminCreditOverview } from '@/hooks/useAdminCreditOverview';
import { Coins, Users, TrendingUp, AlertTriangle, Plus, Minus, Settings, Crown, Search, X } from 'lucide-react';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { backendClient } from '@/lib/backendClient';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { PricingConfigurationPanel } from './PricingConfigurationPanel';
import { SubscriptionTierManager } from './SubscriptionTierManager';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead } from '@/components/ui/sortable-table-head';

export const CreditManagement: React.FC = () => {
  const { overview, practitionerSummaries, isLoading, refetchOverview } = useAdminCreditOverview();
  const { toast } = useToast();

  // ALL useState calls MUST be at the top before any conditionals
  const [selectedPractitioner, setSelectedPractitioner] = useState<string | null>(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState<string>('');
  const [adjustmentNotes, setAdjustmentNotes] = useState<string>('');
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<keyof typeof practitionerSummaries[0] | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  const handleCreditAdjustment = async (type: 'add' | 'deduct') => {
    if (!selectedPractitioner || !adjustmentAmount) return;
    
    const amount = parseInt(adjustmentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid positive number",
        variant: "destructive",
      });
      return;
    }

    setIsAdjusting(true);
    try {
      const finalAmount = type === 'deduct' ? -amount : amount;
      
      // Insert credit transaction
      const { error: transError } = await backendClient
        .from('credit_transactions')
        .insert({
          practitioner_id: selectedPractitioner,
          transaction_type: 'adjustment',
          credits_amount: finalAmount,
          notes: adjustmentNotes || `Admin ${type === 'add' ? 'added' : 'deducted'} ${amount} credits`,
        });

      if (transError) throw transError;

      // Update practitioner credits
      const practitioner = practitionerSummaries.find(p => p.practitioner_id === selectedPractitioner);
      if (!practitioner) throw new Error('Practitioner not found');

      const newBalance = Math.max(0, practitioner.credits_balance + finalAmount);
      const newUsed = type === 'deduct' ? practitioner.total_credits_used + amount : practitioner.total_credits_used;
      const newPurchased = type === 'add' ? practitioner.total_credits_purchased + amount : practitioner.total_credits_purchased;

      const { error: updateError } = await backendClient
        .from('user_credits')
        .update({
          credits_balance: newBalance,
          total_credits_used: newUsed,
          total_credits_purchased: newPurchased,
        })
        .eq('user_id', selectedPractitioner);

      if (updateError) throw updateError;

      toast({
        title: "Credits Adjusted",
        description: `Successfully ${type === 'add' ? 'added' : 'deducted'} ${amount} credits`,
      });

      setAdjustmentAmount('');
      setAdjustmentNotes('');
      setSelectedPractitioner(null);
      refetchOverview();
    } catch (error) {
      console.error('Credit adjustment error:', error);
      toast({
        title: "Adjustment Failed",
        description: "Failed to adjust credits. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAdjusting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-muted rounded w-24"></div>
                  <div className="h-8 bg-muted rounded w-16"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            <Coins className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No credit data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Manual inline sorting - NO HOOKS
  let displayData = [...practitionerSummaries];

  // Sort first
  if (sortKey && sortDirection) {
    displayData.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortDirection === 'asc'
        ? (aStr < bStr ? -1 : aStr > bStr ? 1 : 0)
        : (aStr > bStr ? -1 : aStr < bStr ? 1 : 0);
    });
  }

  // Filter second
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    displayData = displayData.filter(p =>
      p.practitioner_name.toLowerCase().includes(query) ||
      p.practitioner_email.toLowerCase().includes(query) ||
      p.status.toLowerCase().includes(query)
    );
  }

  const requestSort = (key: keyof typeof practitionerSummaries[0]) => {
    let direction: 'asc' | 'desc' | null = 'asc';
    if (sortKey === key && sortDirection === 'asc') {
      direction = 'desc';
    } else if (sortKey === key && sortDirection === 'desc') {
      direction = null;
    }
    setSortKey(direction ? key : null);
    setSortDirection(direction);
  };

  const getSortIndicator = (key: keyof typeof practitionerSummaries[0]) => {
    return sortKey === key ? sortDirection : null;
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Credit Management</h2>
        <p className="text-muted-foreground">Monitor and manage practitioner credit balances and pricing</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Coins className="h-4 w-4" />
            User Credits
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Pricing Rules
          </TabsTrigger>
          <TabsTrigger value="tiers" className="flex items-center gap-2">
            <Crown className="h-4 w-4" />
            Subscription Tiers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Practitioners</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.totalPractitioners}</div>
            <p className="text-xs text-muted-foreground">With credit accounts</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Credits in Circulation</CardTitle>
            <Coins className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.totalCreditsInCirculation}</div>
            <p className="text-xs text-muted-foreground">Available for use</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Credits Used</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.totalCreditsUsed}</div>
            <p className="text-xs text-muted-foreground">Sessions generated</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Low/Depleted</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overview.practitionersWithLowCredits + overview.practitionersOutOfCredits}
            </div>
            <p className="text-xs text-muted-foreground">
              {overview.practitionersWithLowCredits} low, {overview.practitionersOutOfCredits} depleted
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Practitioner Credit Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Practitioner Credit Status</CardTitle>
          </div>

          {/* Search Bar */}
          {practitionerSummaries.length > 0 && (
            <div className="flex items-center gap-2 mt-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or status..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {displayData.length === 0 ? (
            <div className="text-center py-8">
              {searchQuery ? (
                <>
                  <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg font-medium text-muted-foreground">No practitioners found</p>
                  <p className="text-sm text-muted-foreground">Try adjusting your search query</p>
                </>
              ) : (
                <p className="text-muted-foreground">No practitioner credit data available</p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    onSort={() => requestSort('practitioner_name')}
                    sortDirection={getSortIndicator('practitioner_name')}
                  >
                    Practitioner
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('status')}
                    sortDirection={getSortIndicator('status')}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('credits_balance')}
                    sortDirection={getSortIndicator('credits_balance')}
                    className="text-right"
                  >
                    Balance
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('total_credits_purchased')}
                    sortDirection={getSortIndicator('total_credits_purchased')}
                    className="text-right"
                  >
                    Purchased
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('total_credits_used')}
                    sortDirection={getSortIndicator('total_credits_used')}
                    className="text-right"
                  >
                    Used
                  </SortableTableHead>
                  <SortableTableHead sortable={false} className="text-right">
                    Actions
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayData.map((practitioner) => (
                  <TableRow key={practitioner.practitioner_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{practitioner.practitioner_name}</p>
                        <p className="text-sm text-muted-foreground">{practitioner.practitioner_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          practitioner.status === 'depleted' ? 'destructive' :
                          practitioner.status === 'low' ? 'secondary' : 'default'
                        }
                      >
                        {practitioner.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {practitioner.credits_balance}
                    </TableCell>
                    <TableCell className="text-right">
                      {practitioner.total_credits_purchased}
                    </TableCell>
                    <TableCell className="text-right">
                      {practitioner.total_credits_used}
                    </TableCell>
                    <TableCell className="text-right">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedPractitioner(practitioner.practitioner_id)}
                          >
                            Adjust Credits
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Adjust Credits for {practitioner.practitioner_name}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="amount">Amount</Label>
                              <Input
                                id="amount"
                                type="number"
                                placeholder="Enter amount"
                                value={adjustmentAmount}
                                onChange={(e) => setAdjustmentAmount(e.target.value)}
                                min="1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="notes">Notes (optional)</Label>
                              <Textarea
                                id="notes"
                                placeholder="Reason for adjustment..."
                                value={adjustmentNotes}
                                onChange={(e) => setAdjustmentNotes(e.target.value)}
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleCreditAdjustment('add')}
                                disabled={isAdjusting || !adjustmentAmount}
                                className="flex-1"
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add Credits
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => handleCreditAdjustment('deduct')}
                                disabled={isAdjusting || !adjustmentAmount}
                                className="flex-1"
                              >
                                <Minus className="h-4 w-4 mr-1" />
                                Deduct Credits
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="pricing">
          <PricingConfigurationPanel />
        </TabsContent>

        <TabsContent value="tiers">
          <SubscriptionTierManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};
